import { useEffect, useId, useRef } from "react";
import type { ReactNode } from "react";
import { classNames } from "../internal/classNames";
import { StatusGlyph } from "../internal/StatusGlyph";
import "./Dialog.css";

export type DialogSize = "small" | "medium" | "large";
export type DialogTone = "default" | "danger";

export interface DialogProps {
  open: boolean;
  title: string;
  description?: string;
  size?: DialogSize;
  tone?: DialogTone;
  children?: ReactNode;
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  onClose: () => void;
  closeLabel?: string;
  closeDisabled?: boolean;
  className?: string;
}

export function Dialog({
  open,
  title,
  description,
  size = "small",
  tone = "default",
  children,
  primaryAction,
  secondaryAction,
  onClose,
  closeLabel = "대화상자 닫기",
  closeDisabled = false,
  className,
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      titleRef.current?.focus({ preventScroll: true });
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      aria-describedby={description ? descriptionId : undefined}
      aria-labelledby={titleId}
      className={classNames(
        "cp-dialog",
        `cp-dialog--${size}`,
        `cp-dialog--${tone}`,
        Boolean(children) && "cp-dialog--has-body",
        className,
      )}
      onCancel={(event) => {
        // React also delivers nested native-dialog events to ancestor handlers.
        if (event.target !== event.currentTarget) return;
        event.preventDefault();
        if (!closeDisabled) onClose();
      }}
      onClose={(event) => {
        if (event.target !== event.currentTarget) return;
        if (open) onClose();
      }}
      ref={dialogRef}
    >
      <div className="cp-dialog__header">
        <div className="cp-dialog__heading">
          <h2 className="cp-dialog__title" id={titleId} ref={titleRef} tabIndex={-1}>{title}</h2>
          {description && <p className="cp-dialog__description" id={descriptionId}>{description}</p>}
        </div>
        <button
          aria-label={closeLabel}
          className="cp-dialog__close"
          disabled={closeDisabled}
          onClick={onClose}
          type="button"
        >
          <StatusGlyph name="close" />
        </button>
      </div>
      {children && <div className="cp-dialog__body">{children}</div>}
      {(secondaryAction || primaryAction) && (
        <div className="cp-dialog__actions">
          {secondaryAction}
          {primaryAction}
        </div>
      )}
    </dialog>
  );
}
